import axiosClient, { LONG_RUNNING_TIMEOUT_MS } from './axiosClient'
import type {
  ApiResponse,
  BatterGameResult,
  BattingStats,
  GameInfo,
  GameListItem,
  GameListParams,
  GameManagerCacheMigrationResult,
  GameManagerCopyResult,
  GameManagerEnvQuery,
  GameManagerEnvType,
  GameManagerGame,
  GameManagerJobType,
  GameManagerMigrationCount,
  GameManagerMigrationResult,
  GameManagerScheduleMigrationResult,
  GameManagerSystem,
  GameManagerSystemStatus,
  GameManagerTeam,
  GameRecord,
  GameRoster,
  GameRosterPlayer,
  InningScore,
  LoginRequest,
  LoginResponse,
  Member,
  MenuItem,
  PitchItem,
  PitcherGameResult,
  PitcherPitchDetail,
  PitcherWpa,
  PitchingStats,
  PlayerMemo,
  PlayerReport,
  PositionChange,
  ScoreStat,
  StadiumInfo,
  SystemItem,
  TeamInfo,
  TenantConfig,
  TrainingVideo,
  WinExpectancy,
  Wpa,
} from '@/types'

// ── 설정 ─────────────────────────────────────────────────────
export const configApi = {
  getTenantConfig: () =>
    axiosClient.get<{ result: string; resultSet: TenantConfig }>('/config').then((r) => r.data.resultSet),
}

// ── 인증 ─────────────────────────────────────────────────────
export const authApi = {
  login: (data: LoginRequest) =>
    axiosClient.post<LoginResponse>('/auth/login', data).then((r) => r.data),
  logout: () =>
    axiosClient.post('/auth/logout').then((r) => r.data),
  me: () =>
    axiosClient.get<LoginResponse['user']>('/auth/me').then((r) => r.data),
}

// ── 전체 시스템 목록 (시스템 전환기) ─────────────────────────
export const systemApi = {
  list: () =>
    axiosClient
      .get<{ result: string; resultSet: { systems: SystemItem[] } }>('/v1/system-list')
      .then((r) => r.data.resultSet?.systems ?? []),
}

// ── 현재 시스템의 메뉴 목록 (좌측 사이드바) ──────────────────
export const menuApi = {
  list: () =>
    axiosClient
      .get<{ result: string; resultSet: { menus: MenuItem[] } }>('/v1/menu-list')
      .then((r) => r.data.resultSet?.menus ?? []),
}

// ── 경기 ─────────────────────────────────────────────────────
export const gamesApi = {
  list: ({ yearMonth, gameTypeCode, teamId }: GameListParams) => {
    const [season, month] = yearMonth.split('/')
    return axiosClient.get<ApiResponse<{ resultList: GameListItem[] }>>('/v1/games', {
      params: {
        season,
        month,
        ...(gameTypeCode && { gameTypeCode }),
        ...(teamId && { teamId }),
      },
    }).then((r) => r.data)
  },
  get: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ gameInfo: GameInfo }>>(`/games/${gameInfoId}`).then((r) => r.data),
  create: (data: Partial<GameInfo>) =>
    axiosClient.post<ApiResponse<{ gameInfoId: string }>>('/games', data).then((r) => r.data),
  update: (gameInfoId: string, data: Partial<GameInfo>) =>
    axiosClient.put<ApiResponse<void>>(`/games/${gameInfoId}`, data).then((r) => r.data),
  delete: (gameInfoId: string) =>
    axiosClient.delete<ApiResponse<void>>(`/games/${gameInfoId}`).then((r) => r.data),
  patchComplete: (gameInfoId: string, completeYn: string) =>
    axiosClient.patch<ApiResponse<void>>(`/games/${gameInfoId}/complete`, { completeYn }).then((r) => r.data),
  deleteRoster: (gameInfoId: string) =>
    axiosClient.delete<ApiResponse<void>>(`/games/${gameInfoId}/roster`).then((r) => r.data),
  deleteRecords: (gameInfoId: string) =>
    axiosClient.delete<ApiResponse<void>>(`/games/${gameInfoId}/records`).then((r) => r.data),
  // 라인업
  getRoster: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ rosterList: GameRoster[] }>>(`/games/${gameInfoId}/roster`).then((r) => r.data),
  gameResult: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<unknown>>('/v1/games/gameResult', { params: { gameInfoId } }).then((r) => r.data),
  rosterPlayers: (gameInfoId: string | number, teamId: string | number) =>
    axiosClient.get<ApiResponse<{ playerList: GameRosterPlayer[] }>>(
      '/v1/games/roster/players', { params: { gameInfoId, teamId } }
    ).then((r) => r.data),
}

// ── 기록 ─────────────────────────────────────────────────────
export const recordsApi = {
  listByGame: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ recordList: GameRecord[] }>>(`/games/${gameInfoId}/records`).then((r) => r.data),
  get: (gameInfoId: string, recordId: string) =>
    axiosClient.get<ApiResponse<{ record: GameRecord }>>(`/games/${gameInfoId}/records/${recordId}`).then((r) => r.data),
}

// ── 입력기 전용 (RecorderController) ──────────────────────────
export const recorderApi = {
  init: (gameInfoId: string) =>
    axiosClient.post<ApiResponse<unknown>>('/recorder/init', { gameInfoId }).then((r) => r.data),
  synchronize: (data: unknown) =>
    axiosClient.post<ApiResponse<unknown>>('/recorder/synchronize', data).then((r) => r.data),
}

// ── 통계 ─────────────────────────────────────────────────────
export const statsApi = {
  batting: (params: { year: string; teamId?: string; gameType?: string }) =>
    axiosClient.get<ApiResponse<{ statList: BattingStats[] }>>('/stats/batting', { params }).then((r) => r.data),
  pitching: (params: { year: string; teamId?: string; gameType?: string }) =>
    axiosClient.get<ApiResponse<{ statList: PitchingStats[] }>>('/stats/pitching', { params }).then((r) => r.data),
}

// ── 경기 확장 ─────────────────────────────────────────────────
export const gameViewApi = {
  inningScore: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ scores: InningScore[]; lastInning: Record<string, number>; scoreStat: ScoreStat[] }>>(
      `/games/${gameInfoId}/inning-score`
    ).then((r) => r.data),

  positionChanges: (gameInfoId: string, rosterId: string) =>
    axiosClient.get<ApiResponse<{ positionChangeList: PositionChange[] }>>(
      `/games/${gameInfoId}/position-changes/${rosterId}`
    ).then((r) => r.data),

  winExpectancy: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{
      winExpectancy: WinExpectancy[]
      wpa: { away: Wpa[]; home: Wpa[] }
      pitcherWpa: { away: PitcherWpa[]; home: PitcherWpa[] }
    }>>(`/games/${gameInfoId}/win-expectancy`).then((r) => r.data),

  teams: () =>
    axiosClient.get<ApiResponse<{ teamList: TeamInfo[] }>>('/v1/teams').then((r) => r.data),

  stadiums: () =>
    axiosClient.get<ApiResponse<{ stadiumList: StadiumInfo[] }>>('/v1/stadiums').then((r) => r.data),
}

// ── 기록 뷰 ──────────────────────────────────────────────────
export const recordViewApi = {
  pitches: (gameInfoId: string, params?: { pitcherRosterId?: string; ballCode?: string; inning?: string }) =>
    axiosClient.get<ApiResponse<{ pitchList: PitchItem[] }>>(
      `/games/${gameInfoId}/pitches`, { params }
    ).then((r) => r.data),

  operation: (gameInfoId: string, gameRecordId: string) =>
    axiosClient.get<ApiResponse<{ operationList: Record<string, unknown>[] }>>(
      `/games/${gameInfoId}/records/${gameRecordId}/operation`
    ).then((r) => r.data),

  memos: (gameInfoId: string, membersId?: string) =>
    axiosClient.get<ApiResponse<{ memoList: PlayerMemo[] }>>(
      `/games/${gameInfoId}/memos`, { params: { membersId } }
    ).then((r) => r.data),

  reports: (gameInfoId: string, params?: { membersId?: string; userId?: string; reportType?: string }) =>
    axiosClient.get<ApiResponse<{ reportList: PlayerReport[] }>>(
      `/games/${gameInfoId}/reports`, { params }
    ).then((r) => r.data),

  notes: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<Record<string, unknown>>>(`/games/${gameInfoId}/notes`).then((r) => r.data),

  textViewInning: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ inningList: Record<string, unknown>[] }>>(
      `/games/${gameInfoId}/text-view/inning`
    ).then((r) => r.data),

  textViewPitcherResult: (gameInfoId: string, params: { year: string; teamId: string; gameTypeCode?: string }) =>
    axiosClient.get<ApiResponse<{ pitcherResultList: Record<string, unknown>[] }>>(
      `/games/${gameInfoId}/text-view/pitcher-result`, { params }
    ).then((r) => r.data),

  textViewBatterResult: (gameInfoId: string, params: { year: string; teamId: string; gameTypeCode?: string }) =>
    axiosClient.get<ApiResponse<{ batterResultList: Record<string, unknown>[] }>>(
      `/games/${gameInfoId}/text-view/batter-result`, { params }
    ).then((r) => r.data),
}

// ── 선수 성적 ─────────────────────────────────────────────────
export const memberStatsApi = {
  pitcherResult: (memberId: string, params?: {
    year?: string; gameTypeCode?: string; month?: string
    ballCount?: string; strikeCount?: string; outCount?: string; runnerState?: string
  }) =>
    axiosClient.get<ApiResponse<{ enterInfo: PitcherGameResult[]; pitchDetail: PitcherPitchDetail[] }>>(
      `/members/${memberId}/pitcher-result`, { params }
    ).then((r) => r.data),

  batterResult: (memberId: string, params?: {
    year?: string; gameTypeCode?: string; month?: string
    ballCount?: string; strikeCount?: string; outCount?: string; runnerState?: string
  }) =>
    axiosClient.get<ApiResponse<{ batterResultList: BatterGameResult[] }>>(
      `/members/${memberId}/batter-result`, { params }
    ).then((r) => r.data),
}

// ── 데이터 관리 (관리자) ──────────────────────────────────────
export const dataApi = {
  games: (params?: { year?: string; month?: string; day?: string }) =>
    axiosClient.get<ApiResponse<{ gameList: Record<string, unknown>[] }>>(
      '/data/games', { params }
    ).then((r) => r.data),

  rosterDetail: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ rosterList: Record<string, unknown>[] }>>(
      `/data/games/${gameInfoId}/roster`
    ).then((r) => r.data),

  recordOverview: (gameInfoId: string) =>
    axiosClient.get<ApiResponse<{ recordList: Record<string, unknown>[] }>>(
      `/data/games/${gameInfoId}/record`
    ).then((r) => r.data),

  recordDetail: (gameInfoId: string, gameRecordId: string) =>
    axiosClient.get<ApiResponse<{ record: Record<string, unknown> }>>(
      `/data/games/${gameInfoId}/records/${gameRecordId}`
    ).then((r) => r.data),
}

// ── 선수 CRUD ─────────────────────────────────────────────────
export const playersApi = {
  list: (params?: { teamId?: string; keyword?: string }) =>
    axiosClient.get<ApiResponse<{ memberList: Member[] }>>('/members', { params }).then((r) => r.data),
  create: (data: Partial<Member>) =>
    axiosClient.post<ApiResponse<{ memberId: string }>>('/members', data).then((r) => r.data),
  update: (memberId: string, data: Partial<Member>) =>
    axiosClient.put<ApiResponse<void>>(`/members/${memberId}`, data).then((r) => r.data),
}

// ── 훈련영상 CRUD ─────────────────────────────────────────────
export const trainingVideoApi = {
  list: (params?: { teamId?: string; keyword?: string; page?: number; size?: number }) =>
    axiosClient.get<ApiResponse<{ videoList: TrainingVideo[]; total: number }>>('/training-videos', { params }).then((r) => r.data),
  create: (data: Partial<TrainingVideo>) =>
    axiosClient.post<ApiResponse<{ videoId: string }>>('/training-videos', data).then((r) => r.data),
  update: (videoId: string, data: Partial<TrainingVideo>) =>
    axiosClient.put<ApiResponse<void>>(`/training-videos/${videoId}`, data).then((r) => r.data),
  delete: (videoId: string) =>
    axiosClient.delete<ApiResponse<void>>(`/training-videos/${videoId}`).then((r) => r.data),
}

// ── 전력분석 ──────────────────────────────────────────────────
export const strategyApi = {
  applyToGame: (gameInfoId: string) =>
    axiosClient.post<ApiResponse<void>>(`/games/${gameInfoId}/strategy/apply`).then((r) => r.data),
}

// ── 경기 데이터 관리 (시스템간 복사 / 마이그레이션) ──────────────
export const gameManagerApi = {
  systems: () =>
    axiosClient
      .get<ApiResponse<{ systemList: GameManagerSystem[] }>>('/v1/gameManager/systems')
      .then((r) => r.data),

  // 경기목록은 기준 시스템(하이볼)만 조회한다 — 대상 시스템은 상세에서 대조
  games: ({ yearMonth, gameTypeCode, teamId, envType }: GameListParams & { envType: GameManagerEnvType }) => {
    const [season, month] = yearMonth.split('/')
    return axiosClient.get<ApiResponse<{ resultList: GameManagerGame[] }>>('/v1/gameManager/games', {
      params: {
        season,
        month,
        envType,
        ...(gameTypeCode && { gameTypeCode }),
        ...(teamId && { teamId }),
      },
    }).then((r) => r.data)
  },

  // 팀 목록도 기준 시스템의 원본 스키마(TEAM_INFO)에서 가져온다 — 경기목록과 출처를 맞춘다
  teams: (envType: GameManagerEnvType) =>
    axiosClient
      .get<ApiResponse<{ teamList: GameManagerTeam[] }>>('/v1/gameManager/teams', { params: { envType } })
      .then((r) => r.data),

  // envType 에 'all' 을 주면 운영/베타 현황을 한 번에 받는다
  systemStatuses: (gameInfoId: string | number, envType: GameManagerEnvQuery) =>
    axiosClient
      .get<ApiResponse<{ statusList: GameManagerSystemStatus[] }>>(
        `/v1/gameManager/games/${gameInfoId}/systems`, { params: { envType } },
      )
      .then((r) => r.data),

  // 목록의 여러 경기 x 6개 시스템 조합의 마이그레이션 기록 건수 (경기 ID 가 많아 POST 로 보낸다)
  migrationCounts: (gameInfoIds: (string | number)[], envType: GameManagerEnvQuery) =>
    axiosClient
      .post<ApiResponse<{ countList: GameManagerMigrationCount[] }>>(
        '/v1/gameManager/games/migrationCounts',
        { gameInfoIds, envType },
      )
      .then((r) => r.data),

  // 원본/대상은 (시스템 x 환경) 조합 — 같은 시스템의 real -> beta 복사도 가능하다
  copy: (
    gameInfoId: string | number,
    src: { system: string; envType: GameManagerEnvType },
    tgt: { system: string; envType: GameManagerEnvType },
  ) =>
    axiosClient
      .post<ApiResponse<GameManagerCopyResult>>(
        `/v1/gameManager/games/${gameInfoId}/copy`,
        {
          srcSystem: src.system, srcEnvType: src.envType,
          tgtSystem: tgt.system, tgtEnvType: tgt.envType,
        },
      )
      .then((r) => r.data),

  // 기본정보 보내기 — game_info / game_schedule 만 넘긴다 (원본은 허브만 가능)
  copyInfo: (
    gameInfoId: string | number,
    src: { system: string; envType: GameManagerEnvType },
    tgt: { system: string; envType: GameManagerEnvType },
  ) =>
    axiosClient
      .post<ApiResponse<GameManagerCopyResult>>(
        `/v1/gameManager/games/${gameInfoId}/copy-info`,
        {
          srcSystem: src.system, srcEnvType: src.envType,
          tgtSystem: tgt.system, tgtEnvType: tgt.envType,
        },
      )
      .then((r) => r.data),

  migration: (
    gameInfoId: string | number,
    system: string,
    envType: GameManagerEnvType,
    jobType?: GameManagerJobType,
  ) =>
    axiosClient
      .post<ApiResponse<GameManagerMigrationResult>>(
        `/v1/gameManager/games/${gameInfoId}/migration`,
        { system, envType, jobType },
        // 배치가 동기로 끝까지 도는 경기가 있어 기본 타임아웃(30초)을 쓰지 않는다
        { timeout: LONG_RUNNING_TIMEOUT_MS },
      )
      .then((r) => r.data),

  // 일정 마이그레이션(배치 Job3)은 경기일자 기준이라 경기 ID 없이 날짜로 한 번만 실행한다
  scheduleMigration: (
    gameday: string,
    system: string,
    envType: GameManagerEnvType,
  ) =>
    axiosClient
      .post<ApiResponse<GameManagerScheduleMigrationResult>>(
        '/v1/gameManager/schedules/migration',
        { gameday, system, envType },
        { timeout: LONG_RUNNING_TIMEOUT_MS },
      )
      .then((r) => r.data),

  // 캐시 삭제(배치 Job4)는 basis 가 season 이라 경기/경기일자 없이 시즌으로 한 번만 실행한다
  cacheMigration: (
    season: string,
    system: string,
    envType: GameManagerEnvType,
  ) =>
    axiosClient
      .post<ApiResponse<GameManagerCacheMigrationResult>>(
        '/v1/gameManager/caches/migration',
        { season, system, envType },
        { timeout: LONG_RUNNING_TIMEOUT_MS },
      )
      .then((r) => r.data),
}
